import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useBillingPanels } from '@/presentation/hooks/useBillingPanels';
import { Button, Input } from '@/presentation/components/common';
import { PanelFacturacion } from '@/domain/entities/PanelFacturacion';
import { PeriodDetailModal } from '@/presentation/components/billing/PeriodDetailModal';
import { PeriodExpensesSection } from '@/presentation/components/billing/PeriodExpensesSection';
import { PeriodPaymentsTable } from '@/presentation/components/billing/PeriodPaymentsTable';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';

type BillingMode = 'vigente' | 'historico';
type CurrentTab = 'summary' | 'payments' | 'expenses';
type SortField =
  | 'id'
  | 'periodo'
  | 'fechaInicioPeriodo'
  | 'cantidadClinicasActivas'
  | 'cantidadClinicasNuevas'
  | 'cantidadClinicasDadasDeBaja'
  | 'cantidadClinicasQueAbonaron'
  | 'cantidadClinicasConDeuda'
  | 'dineroRecaudado'
  | 'dineroPendientePago'
  | 'efectividad'
  | 'notas';
type SortDirection = 'asc' | 'desc';
type StatusFilter = 'all' | 'abierto' | 'cerrado';

interface BillingPanelPageProps {
  mode: BillingMode;
}

function resolvePeriodo(panel: PanelFacturacion): string {
  if (panel.periodo && /^\d{4}-\d{2}$/.test(panel.periodo)) return panel.periodo;
  const date = new Date(panel.fechaInicioPeriodo);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

export const BillingPanelPage: React.FC<BillingPanelPageProps> = ({ mode }) => {
  const { t } = useTranslation();
  const { canAdminister } = useAuth();
  const { panels, isLoading, error, refetch } = useBillingPanels();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortField, setSortField] = useState<SortField>('fechaInicioPeriodo');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedPanelForDetail, setSelectedPanelForDetail] = useState<PanelFacturacion | null>(null);
  const [currentTab, setCurrentTab] = useState<CurrentTab>('summary');
  /** Totales vivos del mes vigente (PanelPagos + gastos), no el snapshot PanelFacturacion. */
  const [liveSummary, setLiveSummary] = useState<{
    facturado: number;
    cobrado: number;
    adeudado: number;
    gastado: number;
    resultado: number;
  } | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const formatMoney = (n: number) =>
    `$${n.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const truncateNotas = (text?: string, max = 48) => {
    if (!text?.trim()) return '';
    const trimmed = text.trim();
    return trimmed.length <= max ? trimmed : `${trimmed.slice(0, max)}…`;
  };

  const currentPanel = useMemo(() => {
    if (panels.length === 0) return null;
    return [...panels].sort(
      (a, b) => new Date(b.fechaInicioPeriodo).getTime() - new Date(a.fechaInicioPeriodo).getTime()
    )[0];
  }, [panels]);

  const currentPeriodo = currentPanel ? resolvePeriodo(currentPanel) : null;

  const loadLiveSummary = useCallback(async () => {
    if (mode !== 'vigente' || !currentPeriodo) {
      setLiveSummary(null);
      return;
    }
    try {
      const res = await httpClient.get<{
        facturado: number;
        cobrado: number;
        adeudado: number;
        gastado: number;
        resultado: number;
      }>(`/balance/summary?periodo=${encodeURIComponent(currentPeriodo)}`);
      setLiveSummary({
        facturado: Number(res.data.facturado) || 0,
        cobrado: Number(res.data.cobrado) || 0,
        adeudado: Number(res.data.adeudado) || 0,
        gastado: Number(res.data.gastado) || 0,
        resultado: Number(res.data.resultado) || 0,
      });
    } catch {
      setLiveSummary(null);
    }
  }, [mode, currentPeriodo]);

  useEffect(() => {
    void loadLiveSummary();
  }, [loadLiveSummary]);

  useEffect(() => {
    if (!canAdminister && currentTab === 'expenses') {
      setCurrentTab('summary');
    }
  }, [canAdminister, currentTab]);

  // KPIs del mes vigente: siempre desde cobros/gastos reales (no snapshot PanelFacturacion)
  const kpiEsperado =
    liveSummary != null
      ? liveSummary.facturado
      : (currentPanel?.dineroRecaudado ?? 0) + (currentPanel?.dineroPendientePago ?? 0);
  const kpiRecaudado = liveSummary?.cobrado ?? currentPanel?.dineroRecaudado ?? 0;
  const kpiPendiente = liveSummary?.adeudado ?? currentPanel?.dineroPendientePago ?? 0;
  const kpiPorcentaje =
    kpiEsperado > 0 ? (kpiRecaudado / kpiEsperado) * 100 : 0;
  const historicalPanels = useMemo(() => {
    if (!currentPanel) return panels;
    return panels.filter((p) => p.id !== currentPanel.id);
  }, [panels, currentPanel]);

  const filteredAndSortedPanels = useMemo(() => {
    let filtered = [...historicalPanels];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (panel) =>
          panel.periodo.toLowerCase().includes(term) ||
          (panel.notas && panel.notas.toLowerCase().includes(term))
      );
    }

    if (statusFilter !== 'all') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      filtered = filtered.filter((panel) => {
        const inicio = new Date(panel.fechaInicioPeriodo);
        inicio.setHours(0, 0, 0, 0);
        const fin = new Date(panel.fechaFinPeriodo);
        fin.setHours(23, 59, 59, 999);

        if (statusFilter === 'abierto') {
          return inicio <= today && today <= fin;
        }
        return today < inicio || today > fin;
      });
    }

    filtered.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortField) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'periodo':
          aValue = a.periodo.toLowerCase();
          bValue = b.periodo.toLowerCase();
          break;
        case 'fechaInicioPeriodo':
          aValue = new Date(a.fechaInicioPeriodo).getTime();
          bValue = new Date(b.fechaInicioPeriodo).getTime();
          break;
        case 'cantidadClinicasActivas':
          aValue = a.cantidadClinicasActivas;
          bValue = b.cantidadClinicasActivas;
          break;
        case 'cantidadClinicasNuevas':
          aValue = a.cantidadClinicasNuevas;
          bValue = b.cantidadClinicasNuevas;
          break;
        case 'cantidadClinicasDadasDeBaja':
          aValue = a.cantidadClinicasDadasDeBaja;
          bValue = b.cantidadClinicasDadasDeBaja;
          break;
        case 'cantidadClinicasQueAbonaron':
          aValue = a.cantidadClinicasQueAbonaron;
          bValue = b.cantidadClinicasQueAbonaron;
          break;
        case 'cantidadClinicasConDeuda':
          aValue = a.cantidadClinicasConDeuda;
          bValue = b.cantidadClinicasConDeuda;
          break;
        case 'dineroRecaudado':
          aValue = a.dineroRecaudado;
          bValue = b.dineroRecaudado;
          break;
        case 'dineroPendientePago':
          aValue = a.dineroPendientePago;
          bValue = b.dineroPendientePago;
          break;
        case 'efectividad':
          aValue = a.getEfectividad();
          bValue = b.getEfectividad();
          break;
        case 'notas':
          aValue = (a.notas || '').toLowerCase();
          bValue = (b.notas || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [historicalPanels, searchTerm, statusFilter, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const clearHistoricalFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  const getStatusFilterLabel = (value: StatusFilter): string => {
    if (value === 'abierto') return t('pages.billing.open');
    if (value === 'cerrado') return t('pages.billing.closed');
    return t('pages.billing.all');
  };

  const activeHistoricalFilterChips = [
    ...(searchTerm.trim()
      ? [{
          key: 'search',
          label: `${t('common.search')}: ${searchTerm.trim()}`,
          onRemove: () => setSearchTerm(''),
        }]
      : []),
    ...(statusFilter !== 'all'
      ? [{
          key: 'status',
          label: `${t('pages.billing.status')}: ${getStatusFilterLabel(statusFilter)}`,
          onRemove: () => setStatusFilter('all'),
        }]
      : []),
  ];

  const getEffectivenessIcon = (efectividad: number): string => {
    if (efectividad >= 80) return '📈';
    if (efectividad >= 60) return '➡️';
    return '📉';
  };

  const getEffectivenessColor = (efectividad: number): string => {
    if (efectividad >= 80) return 'text-green-600 dark:text-green-400';
    if (efectividad >= 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const formatDateRange = (inicio: Date, fin: Date): string => {
    const inicioStr = inicio.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
    const finStr = fin.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
    return `${inicioStr} - ${finStr}`;
  };

  if (isLoading && panels.length === 0) {
    return (
      <div className="p-4">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 dark:text-gray-400">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        mode === 'historico'
          ? 'panel-page h-full min-w-0 flex flex-col !pb-4 overflow-hidden'
          : 'panel-page'
      }
    >
      <div className={`panel-page-header ${mode === 'historico' ? 'flex-shrink-0' : ''}`}>
        <div className="min-w-0">
          <h1 className="panel-page-title">
            {mode === 'vigente'
              ? t('pages.billing.currentTitle')
              : t('pages.billing.historicalTitle')}
          </h1>
          <p className="panel-page-subtitle hidden sm:block">
            {mode === 'vigente'
              ? currentPanel
                ? t('pages.billing.currentSubtitleWithRange', {
                    periodo: currentPanel.periodo,
                    rango: formatDateRange(
                      new Date(currentPanel.fechaInicioPeriodo),
                      new Date(currentPanel.fechaFinPeriodo)
                    ),
                  })
                : t('pages.billing.currentSubtitle')
              : t('pages.billing.historicalSubtitle')}
          </p>
          {mode === 'vigente' && currentPanel ? (
            <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400 sm:hidden">
              {currentPanel.periodo}
              {' · '}
              {formatDateRange(
                new Date(currentPanel.fechaInicioPeriodo),
                new Date(currentPanel.fechaFinPeriodo)
              )}
            </p>
          ) : null}
        </div>
      </div>

      {mode === 'vigente' && currentPanel && currentPeriodo && (
        <div className="space-y-3">
          <div className="panel-card overflow-hidden">
            <div className="border-b border-slate-200 bg-slate-50/80 p-2 dark:border-gray-700 dark:bg-gray-800/80">
              <div
                className="inline-flex max-w-full overflow-x-auto rounded-lg border border-slate-200 bg-white p-1 dark:border-gray-700 dark:bg-gray-900"
                role="tablist"
                aria-label={t('pages.billing.currentTabsLabel')}
              >
                {[
                  { key: 'summary' as const, label: t('pages.billing.summaryTab') },
                  { key: 'payments' as const, label: t('pages.billing.paymentsTab') },
                  ...(canAdminister
                    ? [{ key: 'expenses' as const, label: t('pages.billing.expensesTab') }]
                    : []),
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setCurrentTab(tab.key)}
                    role="tab"
                    aria-selected={currentTab === tab.key}
                    className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                      currentTab === tab.key
                        ? 'bg-teal-600 text-white shadow-sm dark:bg-teal-500 dark:text-gray-950'
                        : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-gray-800'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {currentTab === 'summary' ? (
              <div className="p-3 xl:p-4">
                <div className="grid grid-cols-2 gap-2 sm:gap-2.5 md:grid-cols-4 md:gap-3">
                  <div className="panel-stat !p-2.5 sm:!p-3.5 border-l-4 border-slate-400 md:!p-4">
                    <p className="panel-stat-label">{t('pages.billing.totalExpected')}</p>
                    <p className="panel-stat-value !mt-0.5 text-base sm:text-lg md:text-2xl">
                      {formatMoney(kpiEsperado)}
                    </p>
                  </div>
                  <div className="panel-stat !p-2.5 sm:!p-3.5 border-l-4 border-green-500 md:!p-4">
                    <p className="panel-stat-label">{t('pages.billing.totalCollected')}</p>
                    <p className="panel-stat-value !mt-0.5 text-base text-green-600 dark:text-green-400 sm:text-lg md:text-2xl">
                      {formatMoney(kpiRecaudado)}
                    </p>
                  </div>
                  <div className="panel-stat !p-2.5 sm:!p-3.5 border-l-4 border-yellow-500 md:!p-4">
                    <p className="panel-stat-label">{t('pages.billing.totalPending')}</p>
                    <p className="panel-stat-value !mt-0.5 text-base text-yellow-600 dark:text-yellow-400 sm:text-lg md:text-2xl">
                      {formatMoney(kpiPendiente)}
                    </p>
                  </div>
                  <div className="panel-stat !p-2.5 sm:!p-3.5 border-l-4 border-teal-500 md:!p-4">
                    <p className="panel-stat-label">{t('pages.billing.collectedPercentage')}</p>
                    <p className="panel-stat-value !mt-0.5 text-base text-teal-600 dark:text-teal-400 sm:text-lg md:text-2xl">
                      {kpiPorcentaje.toFixed(1)}%
                    </p>
                  </div>
                  {canAdminister && liveSummary ? (
                    <>
                      <div className="panel-stat !p-2.5 sm:!p-3.5 border-l-4 border-orange-500 md:!p-4">
                        <p className="panel-stat-label">{t('pages.billing.expenses.gastado')}</p>
                        <p className="panel-stat-value !mt-0.5 text-base sm:text-lg md:text-2xl">
                          {formatMoney(liveSummary.gastado)}
                        </p>
                      </div>
                      <div className="panel-stat !p-2.5 sm:!p-3.5 border-l-4 border-teal-600 md:!p-4">
                        <p className="panel-stat-label">{t('pages.billing.expenses.resultado')}</p>
                        <p
                          className={`panel-stat-value !mt-0.5 text-base sm:text-lg md:text-2xl ${
                            liveSummary.resultado >= 0
                              ? 'text-teal-600 dark:text-teal-400'
                              : 'text-rose-600 dark:text-rose-400'
                          }`}
                        >
                          {formatMoney(liveSummary.resultado)}
                        </p>
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            ) : null}

            {currentTab === 'payments' ? (
              <PeriodPaymentsTable
                periodo={currentPeriodo}
                embedded
                onTotalsChange={() => {
                  void loadLiveSummary();
                }}
              />
            ) : null}

            {currentTab === 'expenses' && canAdminister ? (
              <PeriodExpensesSection
                periodo={currentPeriodo}
                canEdit
                embedded
                hideHeader
                onChanged={() => void loadLiveSummary()}
              />
            ) : null}
          </div>
        </div>
      )}

      {mode === 'vigente' && !currentPanel && (
        <div className="py-12 text-center text-gray-500">{t('pages.billing.noResults')}</div>
      )}

      {mode === 'historico' && (
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          <div className="flex-shrink-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setFiltersOpen((v) => !v)}
                className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  filtersOpen || activeHistoricalFilterChips.length > 0
                    ? 'border-teal-500/50 bg-teal-50 text-teal-800 dark:border-teal-400/40 dark:bg-teal-900/30 dark:text-teal-200'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700'
                }`}
                aria-expanded={filtersOpen}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M6 10h12M9 16h6" />
                </svg>
                {filtersOpen ? t('common.hideFilters') : t('common.showFilters')}
                {activeHistoricalFilterChips.length > 0 && (
                  <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-teal-600 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-white">
                    {activeHistoricalFilterChips.length}
                  </span>
                )}
                <svg
                  className={`h-3.5 w-3.5 transition-transform ${filtersOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {!filtersOpen && activeHistoricalFilterChips.length > 0 && (
                <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
                  {activeHistoricalFilterChips.map((chip) => (
                    <button
                      key={chip.key}
                      type="button"
                      onClick={chip.onRemove}
                      className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-teal-500/50 bg-teal-50 px-2 py-0.5 text-[11px] font-medium leading-4 text-teal-700 transition-colors hover:bg-teal-100 dark:border-teal-400/50 dark:bg-teal-900/20 dark:text-teal-200 dark:hover:bg-teal-900/40"
                      title={chip.label}
                    >
                      <span className="truncate">{chip.label}</span>
                      <span className="text-sm leading-none" aria-hidden>×</span>
                    </button>
                  ))}
                </div>
              )}

              <p className="ml-auto hidden text-xs tabular-nums text-slate-500 dark:text-slate-400 md:block">
                {filteredAndSortedPanels.length}
                {' / '}
                {historicalPanels.length}
              </p>
            </div>

            {filtersOpen && (
              <div className="rounded-lg border border-slate-200 bg-white p-2 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
                  <div className="grid flex-1 grid-cols-1 gap-2 md:grid-cols-[minmax(18rem,1fr)_minmax(10rem,0.45fr)]">
                    <Input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={t('pages.billing.searchPeriod')}
                      className="h-8 py-1 text-xs"
                      aria-label={t('common.search')}
                      title={t('common.search')}
                    />
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                      className="block h-8 w-full rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      aria-label={t('pages.billing.statusFilter')}
                      title={t('pages.billing.statusFilter')}
                    >
                      <option value="all">{t('pages.billing.all')}</option>
                      <option value="abierto">{t('pages.billing.open')}</option>
                      <option value="cerrado">{t('pages.billing.closed')}</option>
                    </select>
                  </div>

                  <button
                    type="button"
                    onClick={clearHistoricalFilters}
                    disabled={activeHistoricalFilterChips.length === 0}
                    className="inline-flex h-8 w-full items-center justify-center rounded-md border border-slate-300 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:bg-gray-700 dark:text-slate-300 dark:hover:bg-gray-600 dark:hover:text-white lg:w-8"
                    title={t('common.clear')}
                    aria-label={t('common.clear')}
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m-8 0h10" />
                    </svg>
                    <span className="ml-2 text-sm lg:hidden">{t('common.clear')}</span>
                  </button>
                </div>

                {activeHistoricalFilterChips.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {activeHistoricalFilterChips.map((chip) => (
                      <button
                        key={chip.key}
                        type="button"
                        onClick={chip.onRemove}
                        className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-teal-500/50 bg-teal-50 px-2 py-0.5 text-[11px] font-medium leading-4 text-teal-700 transition-colors hover:bg-teal-100 dark:border-teal-400/50 dark:bg-teal-900/20 dark:text-teal-200 dark:hover:bg-teal-900/40"
                        title={chip.label}
                      >
                        <span className="truncate">{chip.label}</span>
                        <span className="text-sm leading-none" aria-hidden>×</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Cards mobile */}
          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto panel-safe-bottom md:hidden">
            {filteredAndSortedPanels.length === 0 ? (
              <div className="panel-card px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400">
                {t('pages.billing.noResults')}
              </div>
            ) : (
              filteredAndSortedPanels.map((panel) => {
                const efectividad = panel.getEfectividad();
                return (
                  <div key={panel.id} className="panel-list-card px-3.5 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-[15px] font-semibold leading-snug text-slate-900 dark:text-white">
                          {panel.periodo}
                        </p>
                        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                          {formatDateRange(
                            new Date(panel.fechaInicioPeriodo),
                            new Date(panel.fechaFinPeriodo)
                          )}
                        </p>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <p className="text-[15px] font-bold tabular-nums leading-snug text-green-600 dark:text-green-400">
                          {formatMoney(panel.dineroRecaudado)}
                        </p>
                        <span className={`panel-chip mt-1 ${getEffectivenessColor(efectividad)}`}>
                          {getEffectivenessIcon(efectividad)} {efectividad.toFixed(1)}%
                        </span>
                      </div>
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <span className="rounded-md bg-slate-50 px-1.5 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-gray-900/50 dark:text-slate-300">
                        {panel.cantidadClinicasActivas} {t('pages.billing.activeClinics').toLowerCase()}
                      </span>
                      <span className="rounded-md bg-yellow-50 px-1.5 py-0.5 text-[11px] font-medium text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200">
                        {formatMoney(panel.dineroPendientePago)} {t('pages.billing.pending').toLowerCase()}
                      </span>
                      {panel.notas?.trim() ? (
                        <span
                          className="max-w-full truncate rounded-md bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-500 dark:bg-gray-900/50 dark:text-slate-400"
                          title={panel.notas}
                        >
                          {truncateNotas(panel.notas, 36)}
                        </span>
                      ) : null}
                    </div>

                    <div className="mt-2.5 flex justify-end border-t border-slate-100 pt-2 dark:border-gray-700/80">
                      <button
                        type="button"
                        onClick={() => setSelectedPanelForDetail(panel)}
                        className="inline-flex h-8 items-center rounded-full border border-teal-200 bg-teal-50 px-3 text-[11px] font-medium text-teal-800 shadow-sm transition hover:bg-teal-100 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-200 dark:hover:bg-teal-900/50"
                      >
                        {t('pages.billing.view')}
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Tabla md+ */}
          <div className="panel-table-wrap hidden min-h-0 flex-1 overflow-auto md:block">
            <table className="min-w-[980px] w-full table-fixed divide-y divide-gray-200 text-sm dark:divide-gray-700">
              <colgroup>
                <col className="w-[12%]" />
                <col className="w-[15%]" />
                <col className="w-[15%]" />
                <col className="w-[15%]" />
                <col className="w-[13%]" />
                <col className="w-[13%]" />
                <col className="w-[10%]" />
                <col className="w-[7%]" />
              </colgroup>
              <thead className="panel-thead sticky top-0 z-10">
                <tr>
                  <th
                    className="cursor-pointer whitespace-nowrap bg-slate-50 px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-600"
                    onClick={() => handleSort('periodo')}
                  >
                    {t('pages.billing.period')}
                    {renderSortIcon('periodo')}
                  </th>
                  <th
                    className="cursor-pointer whitespace-nowrap bg-slate-50 px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-600"
                    onClick={() => handleSort('fechaInicioPeriodo')}
                  >
                    {t('pages.billing.dateRange')}
                    {renderSortIcon('fechaInicioPeriodo')}
                  </th>
                  <th
                    className="cursor-pointer whitespace-nowrap bg-slate-50 px-3 py-3 text-right text-xs font-medium uppercase tracking-wider tabular-nums text-gray-500 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-600"
                    onClick={() => handleSort('cantidadClinicasActivas')}
                  >
                    {t('pages.billing.activeShort')}
                    {renderSortIcon('cantidadClinicasActivas')}
                  </th>
                  <th
                    className="cursor-pointer whitespace-nowrap bg-slate-50 px-3 py-3 text-right text-xs font-medium uppercase tracking-wider tabular-nums text-gray-500 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-600"
                    onClick={() => handleSort('cantidadClinicasQueAbonaron')}
                  >
                    {t('pages.billing.paidShort')}
                    {renderSortIcon('cantidadClinicasQueAbonaron')}
                  </th>
                  <th
                    className="cursor-pointer whitespace-nowrap bg-slate-50 px-3 py-3 text-right text-xs font-medium uppercase tracking-wider tabular-nums text-gray-500 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-600"
                    onClick={() => handleSort('dineroRecaudado')}
                  >
                    {t('pages.billing.collected')}
                    {renderSortIcon('dineroRecaudado')}
                  </th>
                  <th
                    className="cursor-pointer whitespace-nowrap bg-slate-50 px-3 py-3 text-right text-xs font-medium uppercase tracking-wider tabular-nums text-gray-500 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-600"
                    onClick={() => handleSort('dineroPendientePago')}
                  >
                    {t('pages.billing.pending')}
                    {renderSortIcon('dineroPendientePago')}
                  </th>
                  <th
                    className="cursor-pointer whitespace-nowrap bg-slate-50 px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-600"
                    onClick={() => handleSort('efectividad')}
                  >
                    {t('pages.billing.effectiveness')}
                    {renderSortIcon('efectividad')}
                  </th>
                  <th className="whitespace-nowrap bg-slate-50 px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:bg-gray-900 dark:text-gray-300">
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                {filteredAndSortedPanels.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                      {t('pages.billing.noResults')}
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedPanels.map((panel) => (
                    <tr key={panel.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="whitespace-nowrap px-3 py-3 font-medium text-gray-900 dark:text-white">
                        {panel.periodo}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-gray-500 dark:text-gray-400">
                        {formatDateRange(
                          new Date(panel.fechaInicioPeriodo),
                          new Date(panel.fechaFinPeriodo)
                        )}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-gray-900 dark:text-white">
                        <div className="font-semibold">{panel.cantidadClinicasActivas}</div>
                        <div className="text-[11px] leading-4 text-gray-500 dark:text-gray-400">
                          {t('pages.billing.newShort')}: {panel.cantidadClinicasNuevas}
                          {' · '}
                          {t('pages.billing.deactivatedShort')}: {panel.cantidadClinicasDadasDeBaja}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-gray-900 dark:text-white">
                        <div className="font-semibold">{panel.cantidadClinicasQueAbonaron}</div>
                        <div className="text-[11px] leading-4 text-gray-500 dark:text-gray-400">
                          {t('pages.billing.debtShort')}: {panel.cantidadClinicasConDeuda}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-right font-semibold tabular-nums text-green-600 dark:text-green-400">
                        {formatMoney(panel.dineroRecaudado)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-right font-semibold tabular-nums text-yellow-600 dark:text-yellow-400">
                        {formatMoney(panel.dineroPendientePago)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-right">
                        <span
                          className={`font-semibold ${getEffectivenessColor(panel.getEfectividad())}`}
                        >
                          {getEffectivenessIcon(panel.getEfectividad())}{' '}
                          {panel.getEfectividad().toFixed(1)}%
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-right font-medium">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedPanelForDetail(panel)}
                        >
                          {t('pages.billing.view')}
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <p className="text-red-800 dark:text-red-200">{error.message}</p>
        </div>
      )}

      {selectedPanelForDetail && (
        <PeriodDetailModal
          isOpen={!!selectedPanelForDetail}
          panel={selectedPanelForDetail}
          onClose={() => setSelectedPanelForDetail(null)}
        />
      )}
    </div>
  );
};

export const BillingCurrentPeriodPage: React.FC = () => (
  <BillingPanelPage mode="vigente" />
);

export const BillingHistoryPage: React.FC = () => (
  <BillingPanelPage mode="historico" />
);

